package com.schoolconnect.app.feature.academicyears

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.feature.common.ApiSchoolDropdown
import com.schoolconnect.app.designsystem.AppDialogStandardWidth
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.calendar
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.chevron_up
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource

private val AcademicYearModalOverlay = Color(0x730F172A)
private val AcademicYearModalSurface = Color.White
private val AcademicYearModalInputBorder = AcademicYearsBorder
private val AcademicYearModalInputBorderFocused = AcademicYearsPrimary
private val AcademicYearModalInputBorderError = Color(0xFFDC2626)
private val AcademicYearModalFieldBg = Color.White
private val AcademicYearModalMuted = AcademicYearsMuted
private val AcademicYearModalNavy = AcademicYearsNavy
private val AcademicYearModalPanelBorder = AcademicYearsBorder
private val AcademicYearModalPanelSurface = Color.White
private val AcademicYearModalSelectedBg = Color(0xFFEAF1FF)
private val AcademicYearDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


private val AcademicYearModalShape = RoundedCornerShape(12.dp)
private val AcademicYearModalFieldShape = RoundedCornerShape(8.dp)
private val AcademicYearModalDropdownShape = RoundedCornerShape(12.dp)

@Composable
internal fun AcademicYearEditorModal(
    draft: AcademicYearEditorDraft,
    schoolOptions: List<String>,
    onDismiss: () -> Unit,
    onSave: () -> Unit,
    onAcademicYearChanged: (String) -> Unit,
    onSchoolChanged: (String) -> Unit,
    onStartDateChanged: (String) -> Unit,
    onEndDateChanged: (String) -> Unit,
    onStatusChanged: (AcademicYearStatus) -> Unit,
    isSaving: Boolean = false,
    apiError: String = ""
) {
    var hasSubmitted by remember { mutableStateOf(false) }
    var schoolQuery by remember { mutableStateOf("") }
    var statusExpanded by remember { mutableStateOf(false) }

    val validationErrors = remember(
        hasSubmitted,
        draft.academicYear,
        draft.school,
        draft.startDate,
        draft.endDate,
    ) {
        validateAcademicYearDraft(
            academicYear = draft.academicYear,
            school = draft.school,
            startDate = draft.startDate,
            endDate = draft.endDate,
            status = draft.status,
            showErrors = hasSubmitted
        )
    }

    LaunchedEffect(draft.id) {
        schoolQuery = ""
        hasSubmitted = false
        statusExpanded = false
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) onDismiss() },
        dismissOnClickOutside = false,
        colors = AcademicYearDialogColors,
        isLoading = isSaving,
        loadingTitle = "Adding academic year"
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AcademicYearModalHeader(onDismiss = { if (!isSaving) onDismiss() })
            AppDialogDivider(colors = AcademicYearDialogColors)
            AppDialogValidationSummary(
                errors = listOf(
                    validationErrors.academicYear,
                    validationErrors.school,
                    validationErrors.startDate,
                    validationErrors.endDate,
                ),
                colors = AcademicYearDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )
            AppDialogErrorMessage(
                message = apiError,
                colors = AcademicYearDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )

            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) { compact ->
                val scrollState = rememberScrollState()
                val bodyPaddingHorizontal = if (compact) 20.dp else 28.dp
                val bodyPaddingVertical = if (compact) 24.dp else 24.dp
                val columnGap = if (compact) 20.dp else 28.dp
                val fieldGap = if (compact) 20.dp else 24.dp

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(
                            start = bodyPaddingHorizontal,
                            top = bodyPaddingVertical,
                            end = bodyPaddingHorizontal,
                            bottom = bodyPaddingVertical
                        ),
                    verticalArrangement = Arrangement.spacedBy(fieldGap)
                ) {
                            if (compact) {
                                Column(verticalArrangement = Arrangement.spacedBy(fieldGap)) {
                                    AcademicYearSchoolField(
                                        draft = draft,
                                        schoolQuery = schoolQuery,
                                        schoolOptions = schoolOptions,
                                        error = validationErrors.school,
                                        onQueryChange = { schoolQuery = it },
                                        onSchoolChanged = onSchoolChanged,
                                        onDismiss = { schoolQuery = "" }
                                    )
                                    AcademicYearFieldColumn(
                                        label = "Start Date",
                                        required = true,
                                        error = validationErrors.startDate,
                                        field = {
                                            AcademicYearDateField(
                                                value = draft.startDate,
                                                placeholder = "DD MMM YYYY",
                                                onValueChange = onStartDateChanged
                                            )
                                        }
                                    )
                                    AcademicYearFieldColumn(
                                        label = "End Date",
                                        required = true,
                                        error = validationErrors.endDate,
                                        field = {
                                            AcademicYearDateField(
                                                value = draft.endDate,
                                                placeholder = "DD MMM YYYY",
                                                onValueChange = onEndDateChanged
                                            )
                                        }
                                    )
                                    AcademicYearGeneratedField(
                                        value = draft.academicYear,
                                        error = validationErrors.academicYear
                                    )
                                    AcademicYearFieldColumn(
                                        label = "Academic Year Status",
                                        required = true,
                                        error = validationErrors.status,
                                        field = {
                                            AcademicYearStatusSelectField(
                                                value = draft.status,
                                                expanded = statusExpanded,
                                                onToggleExpanded = { statusExpanded = !statusExpanded },
                                                onDismissExpanded = { statusExpanded = false },
                                                onSelect = {
                                                    statusExpanded = false
                                                    onStatusChanged(it)
                                                }
                                            )
                                        }
                                    )
                                }
                            } else {
                                Column(verticalArrangement = Arrangement.spacedBy(fieldGap)) {
                                    AcademicYearSchoolField(
                                        draft = draft,
                                        schoolQuery = schoolQuery,
                                        schoolOptions = schoolOptions,
                                        error = validationErrors.school,
                                        onQueryChange = { schoolQuery = it },
                                        onSchoolChanged = onSchoolChanged,
                                        onDismiss = { schoolQuery = "" }
                                    )

                                    Row(horizontalArrangement = Arrangement.spacedBy(columnGap)) {
                                        AcademicYearFieldColumn(
                                            modifier = Modifier.weight(1f),
                                            label = "Start Date",
                                            required = true,
                                            error = validationErrors.startDate,
                                            field = {
                                                AcademicYearDateField(
                                                    value = draft.startDate,
                                                    placeholder = "DD MMM YYYY",
                                                    onValueChange = onStartDateChanged
                                                )
                                            }
                                        )
                                        AcademicYearFieldColumn(
                                            modifier = Modifier.weight(1f),
                                            label = "End Date",
                                            required = true,
                                            error = validationErrors.endDate,
                                            field = {
                                                AcademicYearDateField(
                                                    value = draft.endDate,
                                                    placeholder = "DD MMM YYYY",
                                                    onValueChange = onEndDateChanged
                                                )
                                            }
                                        )
                                    }

                                    Row(horizontalArrangement = Arrangement.spacedBy(columnGap)) {
                                        AcademicYearGeneratedField(
                                            modifier = Modifier.weight(1f),
                                            value = draft.academicYear,
                                            error = validationErrors.academicYear
                                        )
                                        AcademicYearFieldColumn(
                                            modifier = Modifier.weight(1f),
                                            label = "Academic Year Status",
                                            required = true,
                                            error = validationErrors.status,
                                            field = {
                                                AcademicYearStatusSelectField(
                                                    value = draft.status,
                                                    expanded = statusExpanded,
                                                    onToggleExpanded = { statusExpanded = !statusExpanded },
                                                    onDismissExpanded = { statusExpanded = false },
                                                    onSelect = {
                                                        statusExpanded = false
                                                        onStatusChanged(it)
                                                    }
                                                )
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    AcademicYearModalFooter(
                        onCancel = { if (!isSaving) onDismiss() },
                        isSaving = isSaving,
                        onSave = {
                            hasSubmitted = true
                            val errors = validateAcademicYearDraft(
                                academicYear = draft.academicYear,
                                school = draft.school,
                                startDate = draft.startDate,
                                endDate = draft.endDate,
                                status = draft.status,
                                showErrors = true
                            )
                            if (!errors.hasAnyError) {
                                onSave()
                            }
                        }
                    )
                }
            }
        }
@Composable
private fun AcademicYearModalHeader(
    onDismiss: () -> Unit
) {
    AppDialogHeader(
        title = "Add Academic Year",
        subtitle = "Create a new academic year for a school.",
        icon = Res.drawable.calendar,
        closeContentDescription = "Close add academic year modal",
        onClose = onDismiss,
        colors = AcademicYearDialogColors
    )
}

@Composable
private fun AcademicYearModalFooter(
    onCancel: () -> Unit,
    onSave: () -> Unit,
    isSaving: Boolean
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onCancel, enabled = !isSaving),
            AppDialogFooterAction(label = if (isSaving) "Saving..." else "Save", onClick = onSave, style = AppDialogActionStyle.Primary, enabled = !isSaving)
        ),
        colors = AcademicYearDialogColors
    )
}

@Composable
private fun AcademicYearFieldColumn(
    modifier: Modifier = Modifier,
    label: String,
    required: Boolean,
    error: String,
    field: @Composable () -> Unit
) {
    AppDialogFieldColumn(
        label = label,
        modifier = modifier,
        required = required,
        error = error,
        colors = AcademicYearDialogColors,
        labelFontSize = 14.sp,
        field = field
    )
}

@Composable
private fun AcademicYearTextField(
    value: String,
    placeholder: String,
    onValueChange: (String) -> Unit,
    trailingIcon: (@Composable () -> Unit)? = null,
    leadingIcon: (@Composable () -> Unit)? = null,
    readOnly: Boolean = false
) {
    AppDialogTextField(
        value = value,
        placeholder = placeholder,
        onValueChange = onValueChange,
        leadingIcon = leadingIcon,
        trailingIcon = trailingIcon,
        colors = AcademicYearDialogColors,
        fontSize = 14.sp,
        readOnly = readOnly
    )
}

@Composable
private fun AcademicYearGeneratedField(
    value: String,
    error: String,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        AcademicYearFieldColumn(
            label = "Academic Year",
            required = true,
            error = error,
            field = {
                AcademicYearTextField(
                    value = value,
                    placeholder = "Select a start date",
                    onValueChange = {},
                    readOnly = true
                )
            }
        )
        Text(
            text = "Automatically generated from the start date. Format: YYYY-YY (e.g. 2026-27)",
            color = AcademicYearModalMuted,
            fontSize = 12.sp,
            lineHeight = 17.sp
        )
    }
}

@Composable
private fun AcademicYearSchoolField(
    draft: AcademicYearEditorDraft,
    schoolQuery: String,
    schoolOptions: List<String>,
    error: String,
    onQueryChange: (String) -> Unit,
    onSchoolChanged: (String) -> Unit,
    onDismiss: () -> Unit
) {
    AcademicYearFieldColumn(
        label = "School",
        required = true,
        error = error,
        field = {
            AcademicYearSchoolSelectField(
                value = draft.school,
                query = schoolQuery,
                options = schoolOptions.ifEmpty { AcademicYearDefaultSchoolOptions },
                onQueryChange = onQueryChange,
                onValueChange = onSchoolChanged,
                onDismiss = onDismiss
            )
        }
    )
}

@Composable
private fun AcademicYearDateField(
    value: String,
    placeholder: String,
    onValueChange: (String) -> Unit
) {
    AppDialogDateField(
        value = value,
        placeholder = placeholder,
        onValueChange = onValueChange,
        colors = AcademicYearDialogColors,
        fontSize = 14.sp
    )
}

@Composable
private fun AcademicYearStatusSelectField(
    value: AcademicYearStatus,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelect: (AcademicYearStatus) -> Unit
) {
    val options = listOf(
        AcademicYearStatus.ACTIVE,
        AcademicYearStatus.UPCOMING
    )
    AppDialogSelectField(
        value = value.label,
        placeholder = "Select lifecycle",
        expanded = expanded,
        options = options.map { it.label },
        onToggleExpanded = onToggleExpanded,
        onDismissExpanded = onDismissExpanded,
        onSelected = { selected ->
            onSelect(options.first { it.label == selected })
        },
        colors = AcademicYearDialogColors,
        fontSize = 14.sp
    )
}

@Composable
private fun AcademicYearSchoolSelectField(
    value: String,
    query: String,
    options: List<String>,
    onQueryChange: (String) -> Unit,
    onValueChange: (String) -> Unit,
    onDismiss: () -> Unit
) {
    ApiSchoolDropdown(
        selectedLabel = value,
        onSelected = { option ->
            onValueChange(option.name)
            onDismiss()
        }
    )
    return

    var expanded by remember { mutableStateOf(false) }
    var anchorSize by remember { mutableStateOf(IntSize.Zero) }
    val filteredOptions = remember(query, options) {
        val trimmed = query.trim()
        if (trimmed.isBlank()) {
            options
        } else {
            options.filter { it.contains(trimmed, ignoreCase = true) }
        }
    }
    val popupWidth = with(LocalDensity.current) { anchorSize.width.toDp() }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned { anchorSize = it.size }
    ) {
            Surface(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(54.dp)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null
                ) { expanded = !expanded },
            shape = AcademicYearModalFieldShape,
            color = AcademicYearModalFieldBg,
            border = androidx.compose.foundation.BorderStroke(
                1.dp,
                if (expanded) AcademicYearModalInputBorderFocused else AcademicYearModalInputBorder
            )
        ) {
                Row(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = 18.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = value.ifBlank { "Search and select school" },
                        color = if (value.isBlank()) AcademicYearModalMuted else AcademicYearModalNavy,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Medium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f)
                    )
                    Icon(
                        painter = painterResource(if (expanded) Res.drawable.chevron_up else Res.drawable.chevron_down),
                        contentDescription = null,
                        tint = AcademicYearModalNavy,
                        modifier = Modifier.size(20.dp)
                    )
                }
            }

        if (expanded) {
            Popup(
                popupPositionProvider = anchoredPopupPositionProvider(),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true
                ),
                onDismissRequest = {
                    expanded = false
                    onDismiss()
                }
            ) {
                Surface(
                    modifier = Modifier.width(popupWidth),
                    shape = AcademicYearModalDropdownShape,
                    color = AcademicYearModalPanelSurface,
                    border = androidx.compose.foundation.BorderStroke(1.dp, AcademicYearModalPanelBorder),
                    shadowElevation = 16.dp
                ) {
                    Column {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(54.dp)
                                .padding(horizontal = 18.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.search),
                                contentDescription = null,
                                tint = AcademicYearModalMuted,
                                modifier = Modifier.size(20.dp)
                            )
                            Spacer(modifier = Modifier.size(10.dp))
                            BasicTextField(
                                value = query,
                                onValueChange = onQueryChange,
                                singleLine = true,
                                textStyle = TextStyle(
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = AcademicYearModalNavy
                                ),
                                cursorBrush = SolidColor(com.schoolconnect.app.designsystem.AppPrimary),
                                modifier = Modifier.fillMaxWidth()
                            ) { innerTextField ->
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    if (query.isBlank()) {
                                        Text(
                                            text = "Search school...",
                                            color = AcademicYearModalMuted,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Medium,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                    innerTextField()
                                }
                            }
                        }

                        HorizontalDivider(color = AcademicYearsBorder)

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 280.dp)
                                .verticalScroll(rememberScrollState())
                                .padding(vertical = 10.dp)
                        ) {
                            if (filteredOptions.isEmpty()) {
                                Text(
                                    text = "No schools found",
                                    color = AcademicYearModalMuted,
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium,
                                    modifier = Modifier.padding(horizontal = 18.dp, vertical = 14.dp)
                                )
                            } else {
                                filteredOptions.forEach { school ->
                                    val selected = school == value
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(40.dp)
                                            .background(
                                                if (selected) AcademicYearModalSelectedBg else Color.Transparent
                                            )
                                            .clickable {
                                                onValueChange(school)
                                                expanded = false
                                                onDismiss()
                                            }
                                            .padding(horizontal = 18.dp),
                                        contentAlignment = Alignment.CenterStart
                                    ) {
                                        Text(
                                            text = school,
                                            color = AcademicYearModalNavy,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Medium,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun anchoredPopupPositionProvider(): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: androidx.compose.ui.unit.LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0))
            val belowY = anchorBounds.bottom + 10
            val aboveY = anchorBounds.top - popupContentSize.height - 10
            val y = if (belowY + popupContentSize.height <= windowSize.height) {
                belowY
            } else {
                aboveY.coerceAtLeast(0)
            }
            return IntOffset(x, y)
        }
    }
}

private data class AcademicYearFormErrors(
    val academicYear: String = "",
    val school: String = "",
    val startDate: String = "",
    val endDate: String = "",
    val status: String = ""
) {
    val hasAnyError: Boolean
        get() = academicYear.isNotBlank() || school.isNotBlank() || startDate.isNotBlank() || endDate.isNotBlank() || status.isNotBlank()
}

private fun validateAcademicYearDraft(
    academicYear: String,
    school: String,
    startDate: String,
    endDate: String,
    status: AcademicYearStatus,
    showErrors: Boolean
): AcademicYearFormErrors {
    if (!showErrors) return AcademicYearFormErrors()

    val academicYearError = if (academicYear.isBlank()) "Academic year label is required." else ""
    val schoolError = if (school.isBlank()) "School is required." else ""
    val startDateError = if (startDate.isBlank()) "Start date is required." else ""
    val endDateError = if (endDate.isBlank()) "End date is required." else ""
    val statusError = if (status.label.isBlank()) "Academic year lifecycle is required." else ""
    val rangeError = if (startDateError.isBlank() && endDateError.isBlank() && !isValidDateRange(startDate, endDate)) {
        "End date must be on or after start date."
    } else {
        ""
    }

    return AcademicYearFormErrors(
        academicYear = academicYearError,
        school = schoolError,
        startDate = if (rangeError.isNotBlank()) rangeError else startDateError,
        endDate = if (rangeError.isNotBlank()) rangeError else endDateError,
        status = statusError
    )
}

private fun isValidDateRange(startDate: String, endDate: String): Boolean {
    val start = parseDateComparable(startDate) ?: return false
    val end = parseDateComparable(endDate) ?: return false
    return start <= end
}

private fun parseDateComparable(value: String): Int? {
    return appDateInputComparable(value)
}
